// Core Development Requests — the inbox.
//
// Three groups, in the order they deserve attention: WAITING (nobody has
// weighed it — the queue that must not silently grow, oldest first), COMMITTED
// (planned or being built), and CLOSED (shipped or declined). One rule is
// enforced before anything saves: a decline carries a reason, always — the
// requester put their name on an operational need, and "declined" with no why
// reads as "ignored", which kills the channel (see request-loop.ts).

import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { fmtDate } from "@/lib/utils";
import {
  requestInbox, decisionCheck, decisionNotice, STATUS_LABEL,
  type DevRequestFacts, type DevRequestStatus,
} from "@/lib/devrequests/request-loop";
import { workStageLabel, CONCLUDED_STATUSES } from "@/lib/ai/workshop/origin-briefs";

interface Person { id: string; first_name: string | null; last_name: string | null; email: string }

const DECIDABLE: DevRequestStatus[] = ["considering", "planned", "in_progress", "shipped", "declined"];
const STATUS_PILL: Record<string, string> = {
  new: "pill", considering: "pill", planned: "pill-mint",
  in_progress: "pill-warn", shipped: "pill-ok", declined: "pill-bad",
};

export function DevRequestsAdmin() {
  const { employee } = useAuth();
  const [rows, setRows] = useState<DevRequestFacts[]>([]);
  const [people, setPeople] = useState<Record<string, Person>>({});
  const [open, setOpen] = useState<{ id: string; next: DevRequestStatus } | null>(null);
  const [text, setText] = useState("");
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [work, setWork] = useState<Record<string, { status: string; pull_request_url: string | null }>>({});
  const [clarifying, setClarifying] = useState<string | null>(null);
  const [clarifyText, setClarifyText] = useState("");

  useEffect(() => { void load(); }, []);
  async function load() {
    const [{ data: r }, { data: p }] = await Promise.all([
      supabase.from("hq_dev_requests").select("*").order("created_at", { ascending: false }),
      supabase.from("hq_directory").select("id,first_name,last_name,email"),
    ]);
    setRows((r as DevRequestFacts[]) ?? []);
    setPeople(Object.fromEntries(((p as Person[]) ?? []).map(x => [x.id, x])));
    const ids = ((r as DevRequestFacts[]) ?? []).map(x => x.id);
    if (ids.length) {
      const { data: asg } = await supabase.from("ai_engineering_assignments")
        .select("dev_request_id,status,pull_request_url,created_at")
        .in("dev_request_id", ids).order("created_at", { ascending: false });
      const m: Record<string, { status: string; pull_request_url: string | null }> = {};
      for (const a of (asg as { dev_request_id: string; status: string; pull_request_url: string | null }[]) ?? []) {
        if (!m[a.dev_request_id]) m[a.dev_request_id] = a;
      }
      setWork(m);
    }
  }

  const inbox = useMemo(() => requestInbox(rows), [rows]);

  async function decide(r: DevRequestFacts, next: DevRequestStatus, response: string) {
    const verdict = decisionCheck(r.status, next, response);
    if (!verdict.allowed) { setError(verdict.reason ?? "That can't be saved."); return; }
    setBusyId(r.id); setError(null);
    const { error } = await supabase.from("hq_dev_requests").update({
      status: next,
      // A blank box never erases an earlier response.
      ...(response.trim() ? { admin_response: response.trim() } : {}),
      decided_at: new Date().toISOString(),
      decided_by: employee?.id ?? null,
    }).eq("id", r.id);
    setBusyId(null);
    if (error) { setError(error.message); return; }
    // Tell the requester what happened. Fire-and-forget: a notification hiccup
    // must not roll back a decision.
    void supabase.from("hq_notifications").insert({
      recipient_id: r.requester_id, title: "Dev request", category: "system",
      preview: decisionNotice(r.title, next), link: "/dev-requests",
    });
    // Planned / in progress used to hand the request to the AI engineer
    // (hq-work-dispatch). That agent was switched off in the HQ strip-down, so
    // planned now means a person will build it.
    setOpen(null); setText("");
    await load();
  }

  // Cole's conclusion can be right for the words and wrong for the intent —
  // this sends the corrected intent back with his earlier conclusion attached.
  async function clarify(r: DevRequestFacts) {
    if (!clarifyText.trim()) return;
    setBusyId(r.id); setError(null);
    const { data: sess } = await supabase.auth.getSession();
    const res = await fetch("/.netlify/functions/hq-work-dispatch", {
      method: "POST",
      headers: { Authorization: `Bearer ${sess.session?.access_token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ kind: "dev_request", id: r.id, clarify: clarifyText.trim() }),
    });
    const out = await res.json().catch(() => ({}));
    setBusyId(null);
    if (!res.ok) { setError(out.message ?? "That could not be sent."); return; }
    setClarifying(null); setClarifyText("");
    await load();
  }

  const name = (id: string) => {
    const p = people[id];
    return p ? [p.first_name, p.last_name].filter(Boolean).join(" ") || p.email : "Somebody";
  };

  const card = (r: DevRequestFacts) => (
    <div key={r.id} className="card">
      <div className="mb-1 flex flex-wrap items-center gap-2">
        <span className={`pill ${STATUS_PILL[r.status] ?? ""} text-xs`}>{STATUS_LABEL[r.status as DevRequestStatus] ?? r.status}</span>
        {r.app && <span className="pill text-xs">{r.app}</span>}
        {r.area && <span className="pill pill-mint text-xs">{r.area}</span>}
        {work[r.id] && (work[r.id].pull_request_url
          ? <a href={work[r.id].pull_request_url!} target="_blank" rel="noreferrer" className="pill pill-mint text-xs hover:underline">{workStageLabel(work[r.id].status)} ↗</a>
          : <span className="pill pill-mint text-xs">{workStageLabel(work[r.id].status)}</span>)}
        <span className="text-xs text-gray2">{name(r.requester_id)} · {fmtDate(r.created_at)}</span>
      </div>
      <p className="text-sm font-medium">{r.title}</p>
      <p className="mt-1 whitespace-pre-wrap text-sm text-gray2">{r.content}</p>
      {r.admin_response && (
        <div className="mt-2 rounded-lg bg-offwhite px-3 py-2 text-sm">
          <span className="font-medium">Response: </span>{r.admin_response}
        </div>
      )}
      <div className="mt-3 flex flex-wrap gap-2">
        {work[r.id] && CONCLUDED_STATUSES.includes(work[r.id].status) && (
          <button className="btn btn-sm btn-ghost text-teal-dark" disabled={busyId === r.id}
            onClick={() => { setClarifying(clarifying === r.id ? null : r.id); setClarifyText(""); }}>
            That's not quite it — clarify
          </button>
        )}
        {DECIDABLE.filter(s => s !== r.status).map(s => (
          <button key={s} className="btn btn-sm btn-ghost" disabled={busyId === r.id}
            onClick={() => { setOpen({ id: r.id, next: s }); setText(""); }}>
            {STATUS_LABEL[s]}
          </button>
        ))}
      </div>
      {clarifying === r.id && (
        <div className="mt-3 space-y-2 rounded-lg bg-offwhite p-3">
          <label className="label">What did they actually mean? Cole starts over from this, with his earlier conclusion attached.</label>
          <textarea rows={2} autoFocus className="input" value={clarifyText} onChange={e => setClarifyText(e.target.value)} />
          <div className="flex justify-end gap-2">
            <button className="btn btn-sm btn-ghost" onClick={() => { setClarifying(null); setClarifyText(""); }}>Cancel</button>
            <button className="btn btn-sm" disabled={busyId === r.id || !clarifyText.trim()}
              onClick={() => void clarify(r)}>Send it back to Cole</button>
          </div>
        </div>
      )}
      {open?.id === r.id && (
        <div className="mt-3 space-y-2 rounded-lg bg-offwhite p-3">
          <label className="label">
            {open.next === "declined"
              ? "Why not? The requester reads this — required."
              : `Note to the requester (optional) — saving as "${STATUS_LABEL[open.next]}"`}
          </label>
          <textarea rows={2} className="input" value={text} onChange={e => setText(e.target.value)} />
          <div className="flex justify-end gap-2">
            <button className="btn btn-sm btn-ghost" onClick={() => setOpen(null)}>Cancel</button>
            <button className="btn btn-sm" disabled={busyId === r.id}
              onClick={() => void decide(r, open.next, text)}>Save</button>
          </div>
        </div>
      )}
    </div>
  );

  const section = (label: string, hint: string, items: DevRequestFacts[]) => (
    <div className="space-y-3">
      <div>
        <h2 className="text-sm font-semibold uppercase tracking-wider text-gray2">{label} ({items.length})</h2>
        <p className="text-xs text-gray2">{hint}</p>
      </div>
      {items.length === 0
        ? <p className="card py-6 text-center text-sm text-gray2">Nothing here.</p>
        : items.map(card)}
    </div>
  );

  return (
    <div className="mx-auto max-w-3xl space-y-8">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Core Dev Requests</h1>
        <p className="text-sm text-gray2">
          Functionality people need, across the whole suite — HQ, the Hub, kiosk, MRP, the
          storefronts. Every decision notifies the requester; a decline always says why.
        </p>
      </div>
      {error && <div className="rounded-lg bg-bad-bg px-3.5 py-2.5 text-sm text-bad">{error}</div>}
      {section("Waiting on a decision", "Oldest first — the queue that must not silently grow.", inbox.waiting)}
      {section("Committed", "Planned or being built.", inbox.committed)}
      {section("Closed", "Shipped or declined, newest decision first.", inbox.closed)}
    </div>
  );
}
